import React, { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { useAuth } from "../hooks/useAuth";

const OAUTH_SIGN_IN = gql`
  mutation OAuthSignIn($provider: String!, $accessToken: String!) {
    oauthSignIn(provider: $provider, accessToken: $accessToken) {
      token
      user {
        id
        firstName
        lastName
        email
      }
      success
      mfaRequired
    }
  }
`;

const EXCHANGE_INSTAGRAM_CODE = gql`
  mutation ExchangeInstagramCode($code: String!, $redirectUri: String!) {
    exchangeInstagramCode(code: $code, redirectUri: $redirectUri) {
      success
      accessToken
      errors {
        fullMessages
      }
    }
  }
`;

interface InstagramOAuthButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
  children?: React.ReactNode;
}

export default function InstagramOAuthButton({
  onSuccess,
  onError,
  className = "",
  children,
}: InstagramOAuthButtonProps) {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [oauthSignIn] = useMutation(OAUTH_SIGN_IN);
  const [exchangeInstagramCode] = useMutation(EXCHANGE_INSTAGRAM_CODE);

  const handleInstagramLogin = () => {
    setLoading(true);

    // Instagram OAuth flow requires opening a popup window
    const clientId = (import.meta as any).env.VITE_INSTAGRAM_APP_ID;
    const redirectUri = `${window.location.origin}/oauth/instagram/callback`;
    // Use the new Instagram API scopes
    const scope = "instagram_business_basic";

    const authUrl = `https://api.instagram.com/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&response_type=code`;

    // Open popup window for Instagram OAuth
    const popup = window.open(
      authUrl,
      "instagram-oauth",
      "width=600,height=600,scrollbars=yes,resizable=yes"
    );

    // Listen for messages from the popup
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "INSTAGRAM_OAUTH_CODE") {
        try {
          const code = event.data.code;

          // Exchange code for access token using our GraphQL mutation
          const exchangeResult = await exchangeInstagramCode({
            variables: {
              code,
              redirectUri,
            },
          });

          const { success, accessToken, errors } =
            exchangeResult.data?.exchangeInstagramCode || {};

          if (success && accessToken) {
            // Now use the access token to sign in with oauthSignIn mutation
            const signInResult = await oauthSignIn({
              variables: {
                provider: "instagram",
                accessToken,
              },
            });

            if (signInResult.data?.oauthSignIn?.success) {
              // Use auth hook's login method to handle token storage and navigation
              await login(signInResult.data.oauthSignIn.token);

              // Call success callback if provided
              onSuccess?.();
            } else {
              throw new Error("OAuth authentication failed");
            }
          } else {
            const errorMessage =
              errors && errors.length > 0
                ? errors[0].message
                : "Failed to exchange Instagram code for token";
            throw new Error(errorMessage);
          }
        } catch (error) {
          console.error("Instagram OAuth error:", error);
          const errorMessage =
            error instanceof Error ? error.message : "Instagram sign-in failed";
          onError?.(errorMessage);
        } finally {
          setLoading(false);
          popup?.close();
          window.removeEventListener("message", handleMessage);
        }
      } else if (event.data.type === "INSTAGRAM_OAUTH_ERROR") {
        onError?.(event.data.error || "Instagram sign-in failed");
        setLoading(false);
        popup?.close();
        window.removeEventListener("message", handleMessage);
      }
    };

    window.addEventListener("message", handleMessage);

    // Check if popup was closed manually
    const checkClosed = setInterval(() => {
      if (popup?.closed) {
        setLoading(false);
        clearInterval(checkClosed);
        window.removeEventListener("message", handleMessage);
      }
    }, 1000);
  };

  return (
    <button
      onClick={handleInstagramLogin}
      disabled={loading}
      className={`flex items-center justify-center gap-3 w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
      ) : (
        <>
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
              fill="url(#instagram-gradient)"
            />
            <defs>
              <linearGradient
                id="instagram-gradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#833ab4" />
                <stop offset="50%" stopColor="#fd1d1d" />
                <stop offset="100%" stopColor="#fcb045" />
              </linearGradient>
            </defs>
          </svg>
          {children || "Instagram"}
        </>
      )}
    </button>
  );
}
